/**
 * Home.tour — guided-tour step definitions for first-time users.
 *
 * 10-stop walkthrough covering the nav rail, Home content, Play chat,
 * theme toggle, command palette, and feedback. The tour fires once on
 * first arrival (via Onboarding CTA) and can be replayed from the
 * account dropdown.
 *
 * Each step targets a `data-tour="..."` attribute in AppShell.tsx or
 * Home.tsx so the tour is resilient to class renames and DOM reshuffles.
 *
 * Voice: knowledgeable colleague — concise, helpful, never patronizing.
 */
import { createElement, type ReactNode } from 'react';

import type { TourStep } from '@/components/tour/types';

const TOTAL = 10;

function eyebrowText(n: number): string {
  return `TOUR · ${n} of ${TOTAL}`;
}

/**
 * Compose a title with a green italic accent on the key word.
 */
function title(prefix: string, accent: string, suffix: string = '.'): ReactNode {
  return createElement(
    'span',
    null,
    prefix,
    createElement('em', { key: 'accent' }, accent),
    suffix,
  );
}

/**
 * Compose a step body: small uppercase eyebrow line + 1-2 sentence copy.
 */
function body(n: number, copy: string): ReactNode {
  return createElement(
    'span',
    null,
    createElement(
      'span',
      {
        key: 'eyebrow',
        'data-tour-eyebrow': true,
        style: {
          display: 'block',
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase' as const,
          color: 'var(--wayne-ink-3)',
          marginBottom: 8,
          fontWeight: 600,
        },
      },
      eyebrowText(n),
    ),
    createElement('span', { key: 'copy' }, copy),
  );
}

/**
 * 10 stops. Covers: nav rail overview, Home page highlights, Play chat,
 * theme toggle, Cmd+K command palette, and feedback FAB.
 */
export const homeTourSteps: ReadonlyArray<TourStep> = [
  // 1 — Nav rail orientation
  {
    id: 'rail',
    targetSelector: '[data-tour="shell-nav"]',
    title: title('Your ', 'workspace'),
    body: body(
      1,
      'All of Wayne lives on this rail — Home, Chat, Tasks, Connections, Memory, Workflows, and Settings. One click to any surface.',
    ),
    placement: 'right',
    padding: 6,
  },

  // 2 — Home page
  {
    id: 'rail-home',
    targetSelector: '[data-tour="rail-home"]',
    title: title('', 'Home'),
    body: body(
      2,
      'Your morning dashboard. Quick prompts, active agents, and recent conversations — everything to get you moving fast.',
    ),
    placement: 'right',
  },

  // 3 — Play (chat)
  {
    id: 'rail-play',
    targetSelector: '[data-tour="rail-play"]',
    title: title('Start a ', 'conversation'),
    body: body(
      3,
      'Chat with any of the nine agents. Stream responses in real time, watch tools fire, and follow citations back to source.',
    ),
    placement: 'right',
  },

  // 4 — Home welcome content
  {
    id: 'welcome',
    targetSelector: '[data-tour="home-welcome"]',
    title: title('Your day ', 'at a glance'),
    body: body(
      4,
      'A personalized greeting that adapts to what your agents flagged overnight and what is on your plate today.',
    ),
    placement: 'bottom',
    padding: 12,
  },

  // 5 — Quick prompts
  {
    id: 'quick-prompts',
    targetSelector: '[data-tour="home-quick-prompts"]',
    title: title('One-click ', 'prompts'),
    body: body(
      5,
      'Shortcuts for common tasks. Each routes to the best-fit agent automatically — no need to pick one yourself.',
    ),
    placement: 'top',
  },

  // 6 — Theme toggle
  {
    id: 'theme-toggle',
    targetSelector: '[data-tour="shell-theme"]',
    title: title('Light or ', 'dark'),
    body: body(
      6,
      'Toggle between light and dark mode. Your choice persists across sessions.',
    ),
    placement: 'bottom',
  },

  // 7 — Command palette (Cmd+K)
  {
    id: 'cmdk',
    targetSelector: '[data-tour="shell-cmdk"]',
    title: title('The ', 'command palette'),
    body: body(
      7,
      'Press Cmd+K from anywhere to jump to a page, start a chat, or search across sessions. Keyboard-first navigation.',
    ),
    placement: 'bottom',
  },

  // 8 — Slack status
  {
    id: 'slack-status',
    targetSelector: '[data-tour="shell-slack"]',
    title: title('Slack ', 'status'),
    body: body(
      8,
      'Shows whether your Slack integration is connected. Wayne can read channels and post updates on your behalf when linked.',
    ),
    placement: 'bottom',
  },

  // 9 — Feedback FAB
  {
    id: 'feedback',
    targetSelector: '[data-tour="shell-feedback"]',
    title: title('Tell us ', 'anything'),
    body: body(
      9,
      'Bug, idea, or praise — tap this button from any page. Feedback goes directly to the Wayne team.',
    ),
    placement: 'left',
  },

  // 10 — Closing
  {
    id: 'closing',
    targetSelector: '[data-tour="shell-account"]',
    title: title('You are ', 'ready'),
    body: body(
      10,
      'That covers the essentials. Replay this tour any time from your account menu. Press Cmd+K to jump straight into a chat.',
    ),
    placement: 'left',
  },
];

/** Storage key used by Onboarding's "Take the tour" CTA. */
export const TOUR_START_STORAGE_KEY = 'wayne.tour.start';
